import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Button } from '@jobtarget/ui-library';
import OffCanvas from '../common/OffCanvas';
import QuestionnaireSelectorList from './QuestionnaireSelectorList';
import QuestionList from './QuestionList';
import { IJobQuestionnaireWithStats } from '../../types/questionnaire';

interface IQuestionCanvas {
  isOpen: boolean;
  onClose: () => void;
  jobHeaderContent?: ReactNode;
  questionnaires: IJobQuestionnaireWithStats[];
  isLoadingQuestionnaires?: boolean;

  selectedQuestionnaireId: string | null;
  onSelectQuestionnaire: (questionnaireId: string | null) => void;

  selectedQuestionId: number | null;
  onSelectQuestion: (questionId: number | null) => void;

  onApply: () => void;
  onClearFilter: () => void;

  title?: string;
  position?: 'left' | 'right';
}

const QuestionCanvas = ({
  isOpen,
  onClose,
  jobHeaderContent,
  questionnaires,
  isLoadingQuestionnaires,
  selectedQuestionnaireId,
  onSelectQuestionnaire,
  selectedQuestionId,
  onSelectQuestion,
  onApply,
  onClearFilter,
  title = 'Filter by Questions',
  position = 'right',
}: IQuestionCanvas) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const selectedQuestionnaire = useMemo(
    () => questionnaires.find(q => q.questionnaireId === selectedQuestionnaireId) ?? null,
    [questionnaires, selectedQuestionnaireId]
  );

  const hasSelection = selectedQuestionnaireId !== null || selectedQuestionId !== null;
  const canApply = selectedQuestionnaireId !== null;

  const handleApply = () => {
    if (!canApply) return;
    onApply();
  };

  const footer = (
    <div
      className="d-flex justify-content-between align-items-center w-100"
      style={{ gap: '0.5rem' }}
    >
      <Button
        variant="transparent"
        onClick={onClearFilter}
        disabled={!hasSelection}
        style={{ color: '#2f86dd' }}
      >
        Clear Filter
      </Button>
      <Button
        variant="primary"
        onClick={handleApply}
        disabled={!canApply}
      >
        Apply
      </Button>
    </div>
  );

  return (
    <OffCanvas
      isOpen={isOpen}
      onClose={onClose}
      position={position}
      title={title}
      width={isMobile ? '100%' : '80%'}
      mobileWidth="100%"
      zIndex={1050}
      footerContent={footer}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Job header card */}
        {jobHeaderContent && (
          <div
            style={{
              backgroundColor: '#f2f3f7',
              width: 'calc(100% + ' + (isMobile ? '1.5rem' : '2rem') + ')',
              marginLeft: isMobile ? '-0.75rem' : '-1rem',
              marginTop: isMobile ? '-0.75rem' : '-1rem',
              padding: isMobile ? '0.75rem' : '1.5rem',
              boxSizing: 'border-box',
              flexShrink: 0,
              borderBottom: '1px solid #e9ecef',
            }}
          >
            {jobHeaderContent}
          </div>
        )}

        {/* Two-pane body. No marginTop here so the divider can touch the
            bottom of the header card. The divider is rendered as an absolute
            element so it can extend past the body's bottom (into OffCanvas's
            1rem bottom padding) without also extending the left pane. */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '1rem' : '1.5rem',
            minHeight: 0,
            position: 'relative',
          }}
        >
          {/* Vertical divider (desktop only) — absolute so it can reach the
              visual bottom of OffCanvas's content area past its padding. */}
          {!isMobile && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 'calc(40% + 0.75rem)',
                top: 0,
                bottom: '-1rem',
                width: '1px',
                backgroundColor: '#e9ecef',
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Left pane: questionnaire picker */}
          <div
            style={{
              flex: isMobile ? '0 0 auto' : '0 0 40%',
              maxWidth: isMobile ? '100%' : '40%',
              overflowY: isMobile ? 'visible' : 'auto',
              paddingTop: '1rem',
              paddingRight: isMobile ? 0 : '0.2rem',
            }}
          >
            <QuestionnaireSelectorList
              questionnaires={questionnaires}
              selectedQuestionnaireId={selectedQuestionnaireId}
              onSelect={(id) => {
                onSelectQuestionnaire(id);
                // Changing questionnaire resets the question selection because
                // the two questionnaires may share question text but have
                // different question IDs.
                onSelectQuestion(null);
              }}
              isMobile={isMobile}
              isLoading={isLoadingQuestionnaires}
            />
          </div>

          {/* Right pane: questions of the selected questionnaire */}
          <div
            style={{
              flex: 1,
              overflowY: isMobile ? 'visible' : 'auto',
              minWidth: 0,
              // overflow-y: auto clips horizontally at the padding-box edge,
              // which was cutting off the left side of the selected radio's
              // outer ring. We shift the pane 0.5rem left (into the flex gap)
              // and add 0.5rem of internal left padding so content stays in
              // the same visual position but the clip boundary has room for
              // the ring to render.
              marginLeft: isMobile ? 0 : '-0.5rem',
              paddingLeft: isMobile ? 0 : '1rem',
              paddingRight: '1rem',
              paddingTop: '1rem',
            }}
          >
            {selectedQuestionnaire ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.15rem',
                    marginBottom: '1rem',
                  }}
                >
                  <p
                    style={{
                      fontSize: '1.2rem',
                      fontWeight: 500,
                      color: '#1a1a1a',
                      margin: 0,
                    }}
                  >
                    {selectedQuestionnaire.questionnaireName}
                  </p>
                  {selectedQuestionnaire.contentType && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        fontSize: '0.85rem',
                        color: '#6c757d',
                      }}
                    >
                      <i
                        className={
                          selectedQuestionnaire.contentType.toLowerCase() === 'audio'
                            ? 'fal fa-microphone'
                            : 'fal fa-video'
                        }
                      />
                      {selectedQuestionnaire.contentType}
                    </span>
                  )}
                  <hr style={{ margin: '0.6rem 0' }} />
                </div>
                {selectedQuestionnaire.questions.length === 0 ? (
                  <div style={{ color: '#6c757d', padding: '0.5rem' }}>
                    This questionnaire has no questions.
                  </div>
                ) : (
                  <QuestionList
                    questions={selectedQuestionnaire.questions}
                    selectedQuestion={selectedQuestionId}
                    setSelectedQuestion={onSelectQuestion}
                    isMobile={isMobile}
                    getQuestionResponseCount={() => 0}
                    showResponseCount={false}
                  />
                )}
              </>
            ) : (
              <div
                style={{
                  color: '#6c757d',
                  padding: '1rem',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  height: '100%',
                }}
              >
                Select a questionnaire to see its questions.
              </div>
            )}
          </div>
        </div>
      </div>
    </OffCanvas>
  );
};

export default QuestionCanvas;
